import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowRight, CloudOff, Flame, Landmark, OctagonAlert, Radar, Receipt, Repeat, TrendingDown, TriangleAlert, Wallet } from 'lucide-react';
import {
  Button,
  Callout,
  Card,
  EmptyState,
  Faelligkeit,
  Meter,
  Skeleton,
  StatTile,
  TONE_TEXT,
  cn,
  formatDatum,
  formatEuro,
  formatMonat,
} from '@elbklar/ui';

import { SeitenKopf } from '@/components/layout/SeitenKopf';
import { overviewApi, projectionApi, radarApi } from '@/lib/api';
import { prozent, runwayText } from '@/lib/format';
import { zeitraumText } from '@/lib/steuer';
import type { RadarSummary } from '@/types';
import { RunwayChart } from './RunwayChart';
import { hatIst, warnungen } from './warnungen';
import { KIND_TEXT } from '@/features/radar/radarText';
import { StillstandHinweise } from '@/features/radar/StillstandHinweise';

// Links in Zeilen: mindestens 24 px hoch, am Handy 44 px (README „Mobil“).
const linkKlasse = 'inline-flex min-h-6 items-center font-medium text-link underline-offset-2 hover:underline touch:min-h-11';

export function OverviewPage() {
  const uebersicht = useQuery({
    queryKey: ['overview'],
    queryFn: overviewApi.get,
  });

  const projektion = useQuery({
    queryKey: ['projection'],
    queryFn: () => projectionApi.get(),
  });

  // Das Radar ist ein Nebenschauplatz: fehlt es, fehlt nur die Kachel.
  const radar = useQuery({
    queryKey: ['radar', 'summary'],
    queryFn: radarApi.summary,
    retry: false,
  });

  if (uebersicht.isLoading || projektion.isLoading) {
    return (
      <div className="space-y-6">
        <SeitenKopf title="Übersicht" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 rounded-lg" />
          ))}
        </div>
        <Skeleton className="h-80 rounded-lg" />
      </div>
    );
  }

  if (uebersicht.isError || projektion.isError || !uebersicht.data || !projektion.data) {
    return (
      <div className="space-y-6">
        <SeitenKopf title="Übersicht" />
        <EmptyState
          icon={CloudOff}
          title="Übersicht nicht verfügbar"
          description="Die Daten ließen sich nicht laden. Später erneut versuchen."
          action={
            <Button
              variant="primary"
              size="lg"
              loading={uebersicht.isFetching || projektion.isFetching}
              onClick={() => {
                void uebersicht.refetch();
                void projektion.refetch();
              }}
            >
              Erneut laden
            </Button>
          }
        />
      </div>
    );
  }

  const daten = uebersicht.data;
  const verlauf = projektion.data;

  // Eine Warnung oben, die übrigen als Zeilen in „Zu prüfen“ (warnungen.ts).
  const [oben, ...weitere] = warnungen(daten);
  // Ohne Buchungen im Ist-Fenster gibt es kein Ist (U13): Der Burn zeigt den
  // Plan, als „geplant“ beschriftet, und die Ist-Kacheln entfallen.
  const ist = hatIst(daten);
  // Reicht das Geld über den Horizont, ist das die gute Nachricht; sonst
  // trägt der Ton die Warnschwelle (6 Monate) und den Ernstfall (3 Monate).
  // Ohne Kontostand ist die Runway keine Aussage (Start bei null), also ohne Ton.
  const runwayTon = !daten.balance_as_of
    ? undefined
    : daten.runway_months === null
      ? 'success'
      : daten.runway_months <= 3
        ? 'danger'
        : daten.runway_months <= 6
          ? 'warning'
          : undefined;
  const burnAbweichung = weitere.concat(oben ?? []).some((w) => w.schluessel === 'burn_abweichung');

  return (
    <div className="space-y-6">
      <SeitenKopf
        title="Übersicht"
        subtitle={
          daten.balance_as_of
            ? `Kontostand vom ${formatDatum(daten.balance_as_of)}, fortgeschrieben bis ${formatMonat(verlauf.base.points.at(-1)?.label)}.`
            : 'Noch kein Kontostand hinterlegt, die Projektion startet bei null.'
        }
      />

      {oben && (
        <Callout tone={oben.ton} icon={oben.ton === 'danger' ? OctagonAlert : TriangleAlert} title={oben.titel} boxed>
          <p>{oben.text}</p>
          {/* Unterstrichen in Textfarbe: `link` hält auf den -soft-Flächen keine 4,5:1.
              Eigene Zeile mit voller Tippfläche, am Handy 44 px. */}
          <span className="flex flex-wrap gap-x-4">
            {oben.links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="inline-flex min-h-6 items-center font-medium text-foreground underline underline-offset-2 touch:min-h-11"
              >
                {l.label}
              </Link>
            ))}
          </span>
        </Callout>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label="Kontostand"
          icon={Wallet}
          value={formatEuro(daten.current_balance, { cent: true })}
          hint={daten.balance_as_of ? `Stand ${formatDatum(daten.balance_as_of)}` : 'nicht gesetzt'}
        />
        <StatTile
          label="Runway"
          icon={TrendingDown}
          value={runwayText(daten.runway_months, daten.horizon_exceeded, verlauf.horizon_months)}
          tone={runwayTon}
          hint={daten.runway_date ? `leer ab ${formatDatum(daten.runway_date)}` : 'reicht über den Horizont'}
        />
        {ist ? (
          <StatTile
            label="Burn pro Monat"
            icon={Flame}
            value={formatEuro(daten.burn_actual)}
            tone={burnAbweichung ? 'warning' : undefined}
            hint={`geplant ${formatEuro(daten.burn_planned)}`}
          />
        ) : (
          <StatTile
            label="Burn pro Monat, geplant"
            icon={Flame}
            value={formatEuro(daten.burn_planned)}
            hint="aus Fixkosten und Kreditrate, noch keine Buchungen"
          />
        )}
        <StatTile
          label="Restschuld"
          icon={Landmark}
          value={formatEuro(daten.total_debt)}
          hint={daten.loans.length ? `${daten.loans.length} Darlehen` : 'kein Darlehen'}
        />
        <StatTile
          label="Fixkosten pro Monat"
          icon={Repeat}
          value={formatEuro(daten.fixed_costs_monthly)}
          hint={
            Number(daten.fixed_costs_monthly) !== Number(daten.fixed_costs_monthly_net)
              ? `${formatEuro(daten.fixed_costs_monthly_net)} netto`
              : undefined
          }
        />
        {daten.vat_enabled && ist && (
          <StatTile
            label="Umsatzsteuer offen"
            icon={Receipt}
            value={formatEuro(daten.vat_open_balance)}
            tone={Number(daten.vat_open_balance) < 0 ? 'warning' : undefined}
            hint={beschreibeOffeneSteuer(daten.vat_open_balance)}
          />
        )}
        {daten.vat_enabled && (
          <StatTile
            label="Nächste Umsatzsteuer"
            icon={Receipt}
            value={formatEuro(daten.vat_next_amount)}
            hint={
              daten.vat_next_label
                ? `${formatMonat(daten.vat_next_label)} · Voranmeldung ${zeitraumText(daten.vat_period_months)}`
                : `Voranmeldung ${zeitraumText(daten.vat_period_months)}`
            }
          />
        )}
      </div>

      {radar.data && radar.data.open_count > 0 && <RadarKachel daten={radar.data} />}

      <Card
        title="Kontoverlauf"
        description={`Tiefpunkt ${formatEuro(daten.min_balance, { cent: true })} im ${formatMonat(daten.min_balance_label)}`}
      >
        <RunwayChart base={verlauf.base} minBuffer={verlauf.min_buffer} />
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title="Zu prüfen">
          <ul className="space-y-2.5">
            {weitere.map((w) => (
              <li key={w.schluessel} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                <span className={cn('flex items-center gap-2', TONE_TEXT[w.ton])}>
                  <TriangleAlert size={14} strokeWidth={1.75} aria-hidden className="shrink-0" />
                  <span className="text-foreground">{w.titel}</span>
                </span>
                <Link to={w.links[0].to} className={cn('flex items-center gap-1', linkKlasse)}>
                  {w.links[0].label} <ArrowRight size={13} strokeWidth={1.75} aria-hidden />
                </Link>
              </li>
            ))}
            <li className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Entwürfe in der Planung</span>
              {daten.draft_count > 0 ? (
                <Link to="/planung" className={cn('flex items-center gap-1', linkKlasse)}>
                  {daten.draft_count} offen <ArrowRight size={13} strokeWidth={1.75} aria-hidden />
                </Link>
              ) : (
                <span className="text-muted-foreground">keine</span>
              )}
            </li>
            {/* Auch ohne Buchungen im Ist-Fenster: Offene können älter sein (ein
                Auszug vom Vorjahr), und U13 betrifft nur die Ist-Werte. */}
            {(ist || daten.uncategorized_count > 0) && (
              <li className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Unkategorisierte Buchungen</span>
                {daten.uncategorized_count > 0 ? (
                  <Link to="/buchungen?uncategorized=1" className={cn('flex items-center gap-1', linkKlasse)}>
                    {daten.uncategorized_count} offen <ArrowRight size={13} strokeWidth={1.75} aria-hidden />
                  </Link>
                ) : (
                  <span className="text-muted-foreground">keine</span>
                )}
              </li>
            )}
          </ul>
        </Card>

        {ist && (
          <Card title="Größte Ausgabenblöcke">
            {daten.top_categories.length === 0 ? (
              <p className="text-muted-foreground">Im Ist-Fenster keine Ausgaben.</p>
            ) : (
              <ul className="space-y-3">
                {daten.top_categories.map((eintrag) => (
                  <li key={eintrag.category_id ?? 'ohne'}>
                    {/* Ausgaben ohne Vorzeichen: Die Überschrift sagt schon, dass es Abflüsse sind. */}
                    <Meter
                      label={eintrag.name}
                      value={Math.min(Number(eintrag.share) * 100, 100)}
                      valueLabel={`${formatEuro(Math.abs(Number(eintrag.total)))} · ${prozent(eintrag.share)}`}
                    />
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}

        {daten.loans.length > 0 && (
          <Card title="Darlehen">
            <ul className="space-y-2">
              {daten.loans.map((kredit) => (
                <li key={kredit.id} className="flex flex-wrap items-baseline justify-between gap-x-3">
                  <Link to="/kredit" className="flex min-h-6 min-w-0 items-center truncate hover:underline touch:min-h-11">
                    {kredit.name}
                  </Link>
                  <span className="tabular">
                    {formatEuro(kredit.remaining_balance)}
                    <span className="ml-2 t-small text-muted-foreground">{formatEuro(kredit.monthly_payment)} im Monat</span>
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    </div>
  );
}

/**
 * Die offene Steuer in Worten.
 *
 * Positiv heisst: das Finanzamt schuldet Geld — es ist also kuenftiger
 * Zufluss, keine Last. Die Richtung gehoert deshalb ins Wort; die blosse Zahl
 * liest sich in beiden Faellen gleich.
 */
function beschreibeOffeneSteuer(betrag: string): string {
  const wert = Number(betrag);
  if (Math.round(wert) === 0) return 'nichts offen';
  return wert > 0 ? 'Erstattung steht aus' : 'geht ans Finanzamt';
}

/**
 * Die Radar-Kachel: drei Zahlen und die naechsten Fristen. Sie erscheint nur,
 * wenn ueberhaupt etwas Offenes im Radar liegt — sonst waere sie nur Laerm.
 */
function RadarKachel({ daten }: { daten: RadarSummary }) {
  // Vorwarnzeit aus den Radar-Einstellungen; ohne Angabe der Standard der finance-api.
  const wochen = daten.deadline_warning_weeks ?? 6;
  const zeile = [
    `${daten.deadline_soon_count} ${daten.deadline_soon_count === 1 ? 'Frist' : 'Fristen'} in ${wochen} Wochen`,
    `${daten.new_count} neu`,
    `${daten.manual_check_due_count} ${daten.manual_check_due_count === 1 ? 'Prüfauftrag' : 'Prüfaufträge'}`,
  ].join(' · ');
  const naechste = daten.next_deadlines.slice(0, 3);

  return (
    <Card
      title="Radar"
      icon={Radar}
      description={zeile}
      actions={
        <Link to="/radar" className={cn('flex items-center gap-1', linkKlasse)}>
          Zum Radar <ArrowRight size={13} strokeWidth={1.75} aria-hidden />
        </Link>
      }
    >
      {daten.stillstand.length > 0 && <StillstandHinweise stillstand={daten.stillstand} />}

      {naechste.length > 0 && (
        <ul className={cn('space-y-1.5', daten.stillstand.length > 0 && 'mt-4 border-t border-border pt-3')}>
          {naechste.map((frist) => (
            <li key={frist.id}>
              <Link
                to={`/radar/${frist.id}`}
                className="flex min-h-6 items-baseline justify-between gap-3 rounded-sm hover:text-link touch:min-h-11 touch:items-center"
              >
                <span className="min-w-0 truncate">
                  {frist.title}
                  <span className="ml-2 t-small text-muted-foreground">{KIND_TEXT[frist.kind]}</span>
                </span>
                {/* Ein Veranstaltungstermin ist keine Frist: „Termin am …“, ohne Ton. */}
                <Faelligkeit
                  datum={frist.date}
                  relativ
                  termin={frist.termin}
                  vorwarnungTage={wochen * 7}
                  className="shrink-0 t-small"
                />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
